/* eslint-disable react-hooks/exhaustive-deps */
import { FunctionComponent, useEffect, useState, useRef } from 'react';
import FieldGroup from '~/components/controls/Input';
import SelectGroup from '~/components/controls/Select';
import XiomaLogoImg from '~/assets/images/xioma.png';
import Button from '~/components/controls/Button';
import { LoginView } from '~/constants/login';
import { t } from '~/utils/lang';
import history from '~/history';

import { LoginFormStyled, XiomaLogo, TitleStyled, LogoStyled, LogoText, InputsWrapper, Logout } from './styled';
import { Dictionary } from '~/types/general';
import { emptyString } from '~/constants/string';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBranches } from '~/store/main/actions';
import { loginServcicePersonAction } from '~/store/auth/actions';
import { RootState, AppDispatch } from '~/store';
import { branchSelector } from '~/store/main/selectors';
import { getBranch, setBranch } from '~/utils/sessionStorage';

import {
  clearJobCardsNotRunning,
  clearJobCardsRunning,
  clearJobCardReports,
  clearSelectedJobCard,
  clearJobCardFilter,
  resetShowReportSavedModal,
  clearJobCardsRecently,
} from '~/store/jobCard';
import { removeEmployeeId } from '~/utils/sessionStorage';

export interface LoginFormProps {
  logo: string;
}

const defaultDict: Dictionary = {
  label: emptyString,
  value: emptyString,
  timeClockingLevels: [],
};

export const LoginForm: FunctionComponent<LoginFormProps> = ({ logo }) => {
  const dispatch = useDispatch<AppDispatch>();
  const employeeIdinput = useRef<any>(null);

  useEffect(() => {
    if (employeeIdinput.current) {
      const formControl = employeeIdinput?.current?.getElementsByClassName('form-control');
      formControl[0].focus();
    }
  }, [employeeIdinput]);

  const branches = useSelector(branchSelector);
  const company = useSelector((state: RootState) => state.auth.company);
  const tokenBranch = useSelector((state: RootState) => state.auth.branch);

  // Determine initial view: show branch selector if no branch in localStorage
  const getInitialView = () => {
    if (!getBranch()) {
      return LoginView.branch;
    }
    return LoginView.employee;
  };

  const [currentView, setCurrentView] = useState<keyof typeof LoginView>(getInitialView());
  const [selectedBranch, setSelectedBranch] = useState<Dictionary>(defaultDict);

  // Set default branch from token when branches load
  useEffect(() => {
    if (branches.length > 0 && tokenBranch?.value && !selectedBranch.value) {
      const found = branches.find((b: Dictionary) => String(b.value) === String(tokenBranch.value));
      if (found) setSelectedBranch(found);
    }
  }, [branches, tokenBranch?.value]);

  const [employeeId, setEmployeeId] = useState('');
  const [isValidForm, setIsValidForm] = useState(false);
  const [showLogout, setShowLogout] = useState(false);

  const loading = useSelector((state: RootState) => state.auth.loading);

  // Load branches on mount
  useEffect(() => {
    if (company.id) {
      dispatch(fetchBranches(company.id));
    }
  }, [company.id, dispatch]);

  useEffect(() => {
    removeEmployeeId();
    dispatch(clearSelectedJobCard());
    dispatch(clearJobCardFilter());
    dispatch(clearJobCardReports());
    dispatch(clearJobCardsRunning());
    dispatch(clearJobCardsNotRunning());
    dispatch(clearJobCardsRecently());
    dispatch(resetShowReportSavedModal());
  }, []);

  useEffect(() => {
    if (currentView === LoginView.branch) {
      setIsValidForm(Boolean(selectedBranch.value));
    } else {
      setIsValidForm(Boolean(employeeId.length));
    }
  }, [employeeId, currentView, selectedBranch.value]);

  const onBranchChange = (value: Dictionary) => {
    if (value?.value) {
      setSelectedBranch(value);
    }
  };

  const onEmployeeIdChange = (value: string) => {
    setEmployeeId(value);
  };

  const onSelectBranch = () => {
    setBranch(String(selectedBranch.value));
    setCurrentView(LoginView.employee);
  };

  const onConnect = () => {
    dispatch(loginServcicePersonAction(employeeId));
  };

  const onLogout = () => {
    removeEmployeeId();
    dispatch(clearSelectedJobCard());
    dispatch(clearJobCardFilter());
    history.goToLogout();
  };

  const onDoubleClick = () => {
    setShowLogout(!showLogout);
  };

  return (
    <LoginFormStyled>
      {logo ? (
        <LogoStyled logo={logo} onDoubleClick={onDoubleClick} pointer />
      ) : (
        <LogoText onDoubleClick={onDoubleClick} pointer>
          TimeClocking
        </LogoText>
      )}

      <TitleStyled>{t.t('login_page.title')}</TitleStyled>

      {currentView === LoginView.branch && (
        <InputsWrapper>
          <SelectGroup
            value={selectedBranch}
            options={branches}
            label={t.t('login_page.branch')}
            onChange={onBranchChange}
            noOptionsMessage={(a) => 'Loading branches...'}
          />
          <Button title={t.t('login_page.continue')} onClick={onSelectBranch} disabled={!isValidForm} loading={loading} />
        </InputsWrapper>
      )}

      {currentView === LoginView.employee && (
        <InputsWrapper ref={employeeIdinput}>
          <FieldGroup
            value={employeeId}
            label={t.t('login_page.enter_employee_id')}
            onChange={onEmployeeIdChange}
          />
          <Button title={t.t('login_page.connect')} onClick={onConnect} disabled={!isValidForm} loading={loading} />
        </InputsWrapper>
      )}

      <XiomaLogo src={XiomaLogoImg} />

      <Logout onClick={onLogout} show={showLogout} />
    </LoginFormStyled>
  );
};
